import type { PrototypeApiClient } from './apiClient';
import type { PublicTopicSummary } from './apiTopicContracts';

/** The shared initial Home recommendation snapshot; pagination stays in the grid hydrator. */
export interface HomeRecommendedPage {
	topics: PublicTopicSummary[];
	hasMore: boolean;
	nextCursor: string | null;
	totalTopics?: number;
	pageCount?: number;
}

export const HOME_RECOMMENDATION_PAGE_SIZE = 12;

let cachedApi: PrototypeApiClient | null = null;
let cachedPromise: Promise<HomeRecommendedPage> | null = null;
let sessionApi: PrototypeApiClient | null = null;
let session: HomeRecommendedPage | null = null;

export function getHomeRecommendedTopics(api: PrototypeApiClient): Promise<HomeRecommendedPage> {
	if (cachedApi !== api) {
		cachedApi = api;
		cachedPromise = null;
	}
	if (!cachedPromise) {
		const promise = api.topicRecommended({ limit: HOME_RECOMMENDATION_PAGE_SIZE, cursor: null }).then((response) => ({
			topics: response.topics ?? [],
			hasMore: response.hasMore === true,
			nextCursor: response.nextCursor ?? null,
			totalTopics: response.totalTopics,
		}));
		const cached = promise.catch((error: unknown) => {
			if (cachedPromise === cached) cachedPromise = null;
			throw error;
		});
		cachedPromise = cached;
	}
	return cachedPromise;
}

/**
 * The Home grid is intentionally unmounted while another view is active.
 * Keep the already-loaded pages for this viewer so Back restores the same
 * list before re-anchoring the departed card instead of replaying pagination
 * from page one.
 */
export function getHomeRecommendedSession(api: PrototypeApiClient): HomeRecommendedPage | null {
	if (sessionApi !== api || !session) return null;
	return { ...session, topics: [...session.topics] };
}

export function saveHomeRecommendedSession(api: PrototypeApiClient, page: HomeRecommendedPage): void {
	sessionApi = api;
	session = { ...page, topics: [...page.topics] };
}

/** Drops an in-flight initial request without discarding a settled Home session. */
export function clearHomeRecommendedInitialPage(): void {
	cachedApi = null;
	cachedPromise = null;
}

/** Clear the viewer-bound initial recommendation page on scope changes. */
export function clearHomeRecommendedTopics(): void {
	clearHomeRecommendedInitialPage();
	sessionApi = null;
	session = null;
}

export function resetHomeRecommendedTopicsForTest(): void {
	clearHomeRecommendedTopics();
}
